/**
 * adsCosts.ts — what ads cost, per week and per product. Rows come from the ad_costs ledger: the AI that writes ads,
 * and Apify fetches of competitor ads. Apify amounts are estimates from the number of ads returned.
 */
import { z } from "zod";
import { adProductIdSchema } from "./adsProducts.ts";

export const AD_COST_PROVIDERS = ["claude", "grok", "semrush", "apify", "airtable"] as const;
export const AD_COST_PROVIDER_LABEL: Record<(typeof AD_COST_PROVIDERS)[number], string> = {
  claude: "Writing ads (AI)",
  grok: "Research (Grok)",
  semrush: "Keywords (Semrush)",
  apify: "Competitor ads (Apify, estimate)",
  airtable: "Airtable",
};

export const adCostRowSchema = z.object({
  weekStart: z.string(),
  product: adProductIdSchema,
  provider: z.enum(AD_COST_PROVIDERS),
  costUsd: z.number(),
  units: z.number(),
  /** How many of the rows added up here have no price (their cost is not counted in `costUsd`). */
  unpriced: z.number().int().min(0).default(0),
});
export type AdCostRow = z.infer<typeof adCostRowSchema>;

/** Pictures made for ads (the AI scenes), by Google. They are kept in the model ledger, not in the ad cost ledger, so they are listed apart. */
export const adPictureCostSchema = z.object({ weekStart: z.string(), costUsd: z.number().nullable(), units: z.number().int() });

export const adCostReportSchema = z.object({ weeks: z.number().int(), rows: z.array(adCostRowSchema), pictures: z.array(adPictureCostSchema).default([]) });
export type AdCostReport = z.infer<typeof adCostReportSchema>;

/** The Monday `weeks - 1` weeks before the Monday of `weekStart` (YYYY-MM-DD), the oldest week a report shows. */
export function oldestWeek(weekStart: string, weeks: number): string {
  const d = new Date(`${weekStart}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 7 * Math.max(0, weeks - 1));
  return d.toISOString().slice(0, 10);
}
